import { motion } from 'framer-motion';
import { ArrowRight, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

import UltimasVagasBadge from '../../ui/UltimasVagasBadge';
import { useOutrosEncontrosViewModel } from './useOutrosEncontrosViewModel';

const REAIS = (c) =>
  (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtData = (d) =>
  new Date(d).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  });

const OutrosEncontrosView = () => {
  const { eventos, loading } = useOutrosEncontrosViewModel();

  if (!loading && eventos.length === 0) return null;

  return (
    <section className="w-full py-16 md:py-24">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="mb-10 text-center lg:text-left">
          <p className="text-[10px] font-black uppercase tracking-[0.35em] text-wine mb-2">
            Calendário
          </p>
          <h2 className="text-4xl md:text-5xl font-script normal-case text-ink leading-none">
            Leituras complementares
          </h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="aspect-[3/4] rounded-3xl bg-blush-light animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {eventos.map((evento, i) => (
              <motion.div
                key={evento.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <Link
                  to={`/evento-extra/${evento.slug}`}
                  className="group block bg-white rounded-3xl border border-pink-50 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <div className="relative h-44 overflow-hidden">
                    {evento.coverImageBase64 ? (
                      <img
                        src={evento.coverImageBase64}
                        alt={evento.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-rose-soft via-wine to-wine-deep" />
                    )}

                    {/* Verniz sutil pra dar profundidade e garantir contraste do badge */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-black/10" />

                    <span className="absolute top-3 left-3 bg-gradient-to-br from-wine to-wine-deep text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow-lg shadow-wine/30">
                      {fmtData(evento.startDate)}
                    </span>

                    {evento.esgotado && (
                      <span className="absolute top-3 right-3 bg-white/90 text-wine text-[8px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full">
                        Esgotado
                      </span>
                    )}
                    {!evento.esgotado && evento.ultimasVagas && (
                      <div className="absolute top-3 right-3">
                        <UltimasVagasBadge
                          variant="overlay"
                          vagasRestantes={evento.vagasRestantes}
                        />
                      </div>
                    )}
                  </div>

                  <div className="p-5">
                    <h3 className="text-sm font-black text-ink line-clamp-1 group-hover:text-wine transition-colors">
                      {evento.title}
                    </h3>
                    <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1.5">
                      <MapPin size={11} className="text-wine shrink-0" />
                      <span className="line-clamp-1">{evento.location}</span>
                    </p>

                    <div className="flex items-center justify-between mt-4">
                      <span className="text-sm font-black text-ink">
                        {REAIS(evento.amountInCents)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-wine">
                        Quero participar
                        <ArrowRight
                          size={11}
                          className="stroke-[3] transition-transform duration-300 group-hover:translate-x-0.5"
                        />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default OutrosEncontrosView;
